import * as React from "react"
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { useAuthStore } from "@/store"
import { ErrorBoundary } from "@/components/ErrorBoundary"
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary"
import { initGlobalErrorHandlers } from "@/lib/errorLogger"
import { api } from "@/lib/api"
import { Shield, Loader2 } from "lucide-react"

import Sidebar from "@/components/layout/Sidebar"
import Header from "@/components/layout/Header"

import LoginPage from "@/pages/Login"
import Dashboard from "@/pages/Dashboard"
import UsersPage from "@/pages/Users"
import PricingPage from "@/pages/Pricing"
import PanelsPage from "@/pages/Panels"
import SettingsPage from "@/pages/Settings"
import HealthPage from "@/pages/Health"
import WorkflowDesigner from "@/pages/WorkflowDesigner"
import QueueMonitor from "@/pages/QueueMonitor"
import JobScheduler from "@/pages/JobScheduler"
import EventExplorer from "@/pages/EventExplorer"
import NotificationCenter from "@/pages/NotificationCenter"
import LogsPage from "@/pages/Logs"
import AnalyticsPage from "@/pages/Analytics"
import ErrorsPage from "@/pages/Errors"
import LatencyPage from "@/pages/Latency"
import PermissionsPage from "@/pages/Permissions"
import VersionsPage from "@/pages/Versions"
import ResourcesPage from "@/pages/Resources"
import MessagesPage from "@/pages/Messages"
import ButtonsPage from "@/pages/Buttons"
import EmojisPage from "@/pages/Emojis"
import ChannelsPage from "@/pages/Channels"
import ThemesPage from "@/pages/Themes"
import PluginsPage from "@/pages/Plugins"
import SecretsPage from "@/pages/Secrets"
import BackupsPage from "@/pages/Backups"
import AuditPage from "@/pages/Audit"
import CachePage from "@/pages/Cache"
import ProfilePage from "@/pages/Profile"
import ChangePasswordPage from "@/pages/ChangePassword"
import NotFoundPage from "@/pages/NotFound"
import FeaturesPage from "@/pages/Features"
import BotsPage from "@/pages/Bots"
import CommandsPage from "@/pages/Commands"
import MenusPage from "@/pages/Menus"
import ServersPage from "@/pages/Servers"
import NodesPage from "@/pages/Nodes"
import ReferralsPage from "@/pages/Referrals"
import TransactionsPage from "@/pages/Transactions"
import OrdersPage from "@/pages/Orders"
import PaymentsPage from "@/pages/Payments"
import CouponsPage from "@/pages/Coupons"
import MetricsPage from "@/pages/Metrics"

initGlobalErrorHandlers()
const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 30000, retry: 1 } } })

function useAuthValidation() {
  const token = useAuthStore((s) => s.token)
  const setUser = useAuthStore((s) => s.setUser)
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated)
  const setLoading = useAuthStore((s) => s.setLoading)
  const logout = useAuthStore((s) => s.logout)
  React.useEffect(() => {
    const validate = async () => {
      if (!token) { setLoading(false); return }
      try { api.setToken(token); const r = await api.me(); if (r.success && r.user) { setUser(r.user); setAuthenticated(true) } else { logout() } } catch { logout() } finally { setLoading(false) }
    }
    validate()
  }, [])
}

function ProtectedLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false)
  
  return (
    <div style={{ display: 'flex', height: '100vh', background: '#0F172A' }}>
      <Sidebar onCollapse={setSidebarCollapsed} collapsed={sidebarCollapsed} />
      <div style={{ flex: 1, marginLeft: sidebarCollapsed ? 72 : 240, display: 'flex', flexDirection: 'column', overflow: 'hidden', transition: 'margin-left 200ms ease-out' }}>
        <Header />
        <main style={{ flex: 1, overflow: 'auto' }}>
          <Routes>
            <Route path="/" element={<RouteErrorBoundary routeName="Dashboard"><Dashboard /></RouteErrorBoundary>} />
            <Route path="/users" element={<RouteErrorBoundary routeName="Users"><UsersPage /></RouteErrorBoundary>} />
            <Route path="/pricing" element={<RouteErrorBoundary routeName="Pricing"><PricingPage /></RouteErrorBoundary>} />
            <Route path="/plans" element={<RouteErrorBoundary routeName="Plans"><PricingPage /></RouteErrorBoundary>} />
            <Route path="/panels" element={<RouteErrorBoundary routeName="Panels"><PanelsPage /></RouteErrorBoundary>} />
            <Route path="/settings" element={<RouteErrorBoundary routeName="Settings"><SettingsPage /></RouteErrorBoundary>} />
            <Route path="/health" element={<RouteErrorBoundary routeName="Health"><HealthPage /></RouteErrorBoundary>} />
            <Route path="/workflows" element={<RouteErrorBoundary routeName="Workflows"><WorkflowDesigner /></RouteErrorBoundary>} />
            <Route path="/queue" element={<RouteErrorBoundary routeName="Queue"><QueueMonitor /></RouteErrorBoundary>} />
            <Route path="/queues" element={<RouteErrorBoundary routeName="Queues"><QueueMonitor /></RouteErrorBoundary>} />
            <Route path="/jobs" element={<RouteErrorBoundary routeName="Jobs"><JobScheduler /></RouteErrorBoundary>} />
            <Route path="/events" element={<RouteErrorBoundary routeName="Events"><EventExplorer /></RouteErrorBoundary>} />
            <Route path="/notifications" element={<RouteErrorBoundary routeName="Notifications"><NotificationCenter /></RouteErrorBoundary>} />
            <Route path="/orders" element={<RouteErrorBoundary routeName="Orders"><OrdersPage /></RouteErrorBoundary>} />
            <Route path="/payments" element={<RouteErrorBoundary routeName="Payments"><PaymentsPage /></RouteErrorBoundary>} />
            <Route path="/logs" element={<RouteErrorBoundary routeName="Logs"><LogsPage /></RouteErrorBoundary>} />
            <Route path="/analytics" element={<RouteErrorBoundary routeName="Analytics"><AnalyticsPage /></RouteErrorBoundary>} />
            <Route path="/errors" element={<RouteErrorBoundary routeName="Errors"><ErrorsPage /></RouteErrorBoundary>} />
            <Route path="/latency" element={<RouteErrorBoundary routeName="Latency"><LatencyPage /></RouteErrorBoundary>} />
            <Route path="/permissions" element={<RouteErrorBoundary routeName="Permissions"><PermissionsPage /></RouteErrorBoundary>} />
            <Route path="/versions" element={<RouteErrorBoundary routeName="Versions"><VersionsPage /></RouteErrorBoundary>} />
            <Route path="/resources" element={<RouteErrorBoundary routeName="Resources"><ResourcesPage /></RouteErrorBoundary>} />
            <Route path="/messages" element={<RouteErrorBoundary routeName="Messages"><MessagesPage /></RouteErrorBoundary>} />
            <Route path="/buttons" element={<RouteErrorBoundary routeName="Buttons"><ButtonsPage /></RouteErrorBoundary>} />
            <Route path="/emojis" element={<RouteErrorBoundary routeName="Emojis"><EmojisPage /></RouteErrorBoundary>} />
            <Route path="/themes" element={<RouteErrorBoundary routeName="Themes"><ThemesPage /></RouteErrorBoundary>} />
            <Route path="/channels" element={<RouteErrorBoundary routeName="Channels"><ChannelsPage /></RouteErrorBoundary>} />
            <Route path="/plugins" element={<RouteErrorBoundary routeName="Plugins"><PluginsPage /></RouteErrorBoundary>} />
            <Route path="/cache" element={<RouteErrorBoundary routeName="Cache"><CachePage /></RouteErrorBoundary>} />
            <Route path="/features" element={<RouteErrorBoundary routeName="Features"><FeaturesPage /></RouteErrorBoundary>} />
            <Route path="/secrets" element={<RouteErrorBoundary routeName="Secrets"><SecretsPage /></RouteErrorBoundary>} />
            <Route path="/audit" element={<RouteErrorBoundary routeName="Audit"><AuditPage /></RouteErrorBoundary>} />
            <Route path="/backups" element={<RouteErrorBoundary routeName="Backups"><BackupsPage /></RouteErrorBoundary>} />
            <Route path="/profile" element={<RouteErrorBoundary routeName="Profile"><ProfilePage /></RouteErrorBoundary>} />
            <Route path="/change-password" element={<RouteErrorBoundary routeName="ChangePassword"><ChangePasswordPage /></RouteErrorBoundary>} />
            <Route path="/bots" element={<RouteErrorBoundary routeName="Bots"><BotsPage /></RouteErrorBoundary>} />
            <Route path="/commands" element={<RouteErrorBoundary routeName="Commands"><CommandsPage /></RouteErrorBoundary>} />
            <Route path="/menus" element={<RouteErrorBoundary routeName="Menus"><MenusPage /></RouteErrorBoundary>} />
            <Route path="/servers" element={<RouteErrorBoundary routeName="Servers"><ServersPage /></RouteErrorBoundary>} />
            <Route path="/nodes" element={<RouteErrorBoundary routeName="Nodes"><NodesPage /></RouteErrorBoundary>} />
            <Route path="/referrals" element={<RouteErrorBoundary routeName="Referrals"><ReferralsPage /></RouteErrorBoundary>} />
            <Route path="/transactions" element={<RouteErrorBoundary routeName="Transactions"><TransactionsPage /></RouteErrorBoundary>} />
            <Route path="/coupons" element={<RouteErrorBoundary routeName="Coupons"><CouponsPage /></RouteErrorBoundary>} />
            <Route path="/metrics" element={<RouteErrorBoundary routeName="Metrics"><MetricsPage /></RouteErrorBoundary>} />
            <Route path="*" element={<RouteErrorBoundary routeName="NotFound"><NotFoundPage /></RouteErrorBoundary>} />
          </Routes>
        </main>
      </div>
    </div>
  )
}

function LoadingScreen() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0F172A' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#3B82F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Shield size={24} color="#fff" />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#94A3B8' }}>
          <Loader2 size={16} className="animate-spin" /> Loading...
        </div>
      </div>
    </div>
  )
}

function App() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isLoading = useAuthStore((s) => s.isLoading)
  useAuthValidation()
  if (isLoading) return <LoadingScreen />
  return (
    <ErrorBoundary fallbackName="App">
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/*" element={isAuthenticated ? <ProtectedLayout /> : <Navigate to="/login" replace />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App
